import type { TabColorAppearanceMode, TabColorKey } from './tabColorDefinitions.ts';

export type TabColorSwatches = readonly (TabColorKey | null)[];

export type TabColorPalette = {
	/** Flat swatch order; row layout is derived from columnCount. */
	swatches: TabColorSwatches;
	columnCount: number;
	/** Visual appearance resolved for every color key in this palette. */
	appearanceMode: TabColorAppearanceMode;
	/** Whether swatches show their color labels in tooltips. */
	showSwatchTooltips: boolean;
};

/**
 * Legacy M1 color picker swatches. Keep this as-is for the current rollout when
 * the standard color palette is disabled.
 */
export const LEGACY_TAB_COLOR_SWATCHES: TabColorSwatches = [
	null,
	'blue-subtlest',
	'teal-subtlest',
	'green-subtlest',
	'yellow-subtlest',
	'red-subtlest',
	'purple-subtlest',

	'gray-subtler',
	'blue-subtler',
	'teal-subtler',
	'green-subtler',
	'yellow-subtler',
	'red-subtler',
	'purple-subtler',

	'gray-subtle',
	'blue-subtle',
	'teal-subtle',
	'green-subtle',
	'orange-subtle',
	'red-subtle',
	'purple-subtle',
];

/**
 * Standard swatches: the same visible palette size/order as the Panel/Table
 * palette, while continuing to store semantic tab color IDs in tab params.
 *
 * Tabs keeps "No color" as the top-left reset action in place of Panel's White
 * swatch. Row 3 uses existing `*-subtle` persisted IDs and maps them to the
 * standard bold-row appearance through the color definitions.
 */
export const STANDARD_TAB_COLOR_SWATCHES: TabColorSwatches = [
	null,
	'blue-subtlest',
	'teal-subtlest',
	'green-subtlest',
	'lime-subtlest',
	'yellow-subtlest',
	'orange-subtlest',
	'red-subtlest',
	'magenta-subtlest',
	'purple-subtlest',

	'gray-subtler',
	'blue-subtler',
	'teal-subtler',
	'green-subtler',
	'lime-subtler',
	'yellow-subtler',
	'orange-subtler',
	'red-subtler',
	'magenta-subtler',
	'purple-subtler',

	'gray-subtle',
	'blue-subtle',
	'teal-subtle',
	'green-subtle',
	'lime-subtle',
	'yellow-subtle',
	'orange-subtle',
	'red-subtle',
	'magenta-subtle',
	'purple-subtle',
];

export const LEGACY_TAB_COLOR_PALETTE: TabColorPalette = {
	swatches: LEGACY_TAB_COLOR_SWATCHES,
	columnCount: 7,
	appearanceMode: 'legacy',
	showSwatchTooltips: false,
};

export const STANDARD_TAB_COLOR_PALETTE: TabColorPalette = {
	swatches: STANDARD_TAB_COLOR_SWATCHES,
	columnCount: 10,
	appearanceMode: 'standard',
	showSwatchTooltips: true,
};

export const getTabColorPalette = (useStandardColorPalette: boolean): TabColorPalette =>
	useStandardColorPalette ? STANDARD_TAB_COLOR_PALETTE : LEGACY_TAB_COLOR_PALETTE;
